import React, { useState, useEffect } from 'react';
import { assetUrl } from '../utils/assets';
import { Card, DeckStatus, SuggestedPhrases, CardConsequence } from '../types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import TypingAnimation from './TypingAnimation';
import { useSoundEffects } from '../context/SoundEffectsContext';
import '../styles/CardDeck.css';

// Helper to get conditional modifier value from card consequences
const getConditionalModifierValue = (card: Card): number | null => {
  if (!card.consequences) return null;

  const conditionalDiceModifier = card.consequences.find(
    (c: CardConsequence) =>
      c.effect === 'dice_modifier' &&
      c.has_condition === true &&
      c.trigger === 'on_play'
  );

  if (conditionalDiceModifier && conditionalDiceModifier.params?.value) {
    return conditionalDiceModifier.params.value as number;
  }
  return null;
};

interface CardDeckProps {
  deckStatus: DeckStatus;
  suggestedPhrases: SuggestedPhrases;
  isLoading: boolean;
  onSubmitCardAction: (cardName: string, actionPhrase: string) => void;
  isTutorialMode?: boolean;
  onCardSelected?: () => void;
  isInFreeFormStep?: boolean;
  isVisible?: boolean;
  onCardPlayModeChange?: (isInCardPlayMode: boolean) => void;
  tutorialStep?: number;
  onTypingComplete?: () => void;
}

// Interface to track card state (flipped/selected)
interface CardState {
  isFlipped: boolean;
  isSelected: boolean;
}

const CardDeck: React.FC<CardDeckProps> = ({
  deckStatus,
  suggestedPhrases,
  isLoading,
  onSubmitCardAction,
  isTutorialMode = false,
  onCardSelected,
  isInFreeFormStep = false,
  isVisible = true,
  onCardPlayModeChange,
  tutorialStep,
  onTypingComplete
}) => {
  const { playSound } = useSoundEffects();

  // Helper function to format card names (e.g., "alfie_think" -> "Alfie Think")
  const formatCardName = (name: string): string => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const [cardStates, setCardStates] = useState<{[key: string]: CardState}>({});
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [selectedPhrase, setSelectedPhrase] = useState<string>('');
  const [customPhrase, setCustomPhrase] = useState<string>('');
  const [showDiscardShuffle, setShowDiscardShuffle] = useState<boolean>(false);
  const [isTypingComplete, setIsTypingComplete] = useState<boolean>(false);
  const [tutorialPredefinedPhrase, setTutorialPredefinedPhrase] = useState<string>('');

  // Initialize card states whenever hand changes
  useEffect(() => {
    const newCardStates = deckStatus.hand.reduce((acc, card) => {
      acc[card.name] = { isFlipped: false, isSelected: false };
      return acc;
    }, {} as {[key: string]: CardState});
    setCardStates(newCardStates);

    // If the discard pile was shuffled, show the animation
    if (deckStatus.was_shuffled) {
      setShowDiscardShuffle(true);
      setTimeout(() => setShowDiscardShuffle(false), 2000);
    }

    // Reset card play mode when we get a new hand (after loading completes)
    if (!isLoading && deckStatus.hand.length > 0) {
      setIsCardPlayMode(false);
      setSelectedCard(null);
      if (onCardPlayModeChange) {
        onCardPlayModeChange(false);
      }
    }
  }, [deckStatus.hand, deckStatus.was_shuffled, isLoading]);

  // Tutorial step 4: Set predefined phrase when card is selected
  useEffect(() => {
    if (isTutorialMode && tutorialStep === 4 && selectedCard && isInFreeFormStep) {
      const phrases = suggestedPhrases.card_phrases[selectedCard.name] || [];
      if (phrases.length === 1) {
        setTutorialPredefinedPhrase(phrases[0]);
        setIsTypingComplete(false);
      }
    } else {
      setTutorialPredefinedPhrase('');
      setIsTypingComplete(false);
    }
  }, [isTutorialMode, tutorialStep, selectedCard, isInFreeFormStep, suggestedPhrases]);

  const handleCardHover = (cardName: string, isHovering: boolean) => {
    if (!selectedCard) {
      setCardStates(prev => ({
        ...prev,
        [cardName]: { ...prev[cardName], isFlipped: isHovering }
      }));
    }
  };

  // Track whether we're in "card selected" mode where only the selected card is shown
  const [isCardPlayMode, setIsCardPlayMode] = useState<boolean>(false);
  
  // Track cards with animation states
  const [animatingCards, setAnimatingCards] = useState<{[key: string]: boolean}>({});
  const [cardsToDiscard, setCardsToDiscard] = useState<Card[]>([]);
  const [isShaking, setIsShaking] = useState<string | null>(null);
  
  // Function to handle immediate submission of chaos cards
  const handleChaosCardPlay = (card: Card) => {
    if (isLoading) return;
    
    // Mark the card for shaking animation
    setIsShaking(card.name);
    
    // Mark all cards for animation (will be discarded after shake)
    const animStates: {[key: string]: boolean} = {};
    deckStatus.hand.forEach(c => {
      animStates[c.name] = true;
    });
    setAnimatingCards(animStates);
    
    // Gather all cards to discard (including the chaos card)
    const discarding = [...deckStatus.hand];
    setCardsToDiscard(discarding);
    
    // No need to update card states for selection since we're playing immediately
    
    // Notify tutorial context of card selection if in tutorial mode
    if (isTutorialMode) {
      if (onCardSelected) {
        onCardSelected();
      }
    }
    
    // After shake animation completes, submit the card
    setTimeout(() => {
      // Submit with empty action phrase as per the backend requirements for chaos cards
      onSubmitCardAction(card.name, '');
      
      // Clear animation states
      setTimeout(() => {
        setAnimatingCards({});
        setCardsToDiscard([]);
        setIsShaking(null);
      }, 100);
    }, 600); // Match the shake animation duration
  };
  
  const handleCardClick = (card: Card) => {
    if (isLoading) return;

    playSound('card_flip');

    // For chaos cards, play immediately without showing the action selection
    if (card.type === 'chaos') {
      handleChaosCardPlay(card);
      return;
    }
    
    // If a card is already selected and we click it again, deselect it
    if (selectedCard && selectedCard.name === card.name && isCardPlayMode) {
      setSelectedCard(null);
      setSelectedPhrase('');
      setCustomPhrase('');
      setIsCardPlayMode(false);
      if (onCardPlayModeChange) {
        onCardPlayModeChange(false);
      }
      
      // Reset all card states
      const newCardStates = { ...cardStates };
      Object.keys(newCardStates).forEach(name => {
        newCardStates[name].isSelected = false;
      });
      setCardStates(newCardStates);
    } else {
      // Gather cards to discard (all except the selected one)
      const discarding = deckStatus.hand.filter(c => c.name !== card.name);
      setCardsToDiscard(discarding);
      
      // Mark all cards for animation
      const animStates: {[key: string]: boolean} = {};
      deckStatus.hand.forEach(c => {
        animStates[c.name] = true;
      });
      setAnimatingCards(animStates);
      
      // Update card states - only this card is selected
      const newCardStates = { ...cardStates };
      Object.keys(newCardStates).forEach(name => {
        newCardStates[name].isSelected = (name === card.name);
        newCardStates[name].isFlipped = false;
      });
      setCardStates(newCardStates);
      
      // Notify tutorial context of card selection if in tutorial mode
      if (isTutorialMode && onCardSelected) {
        onCardSelected();
      }
      
      // Delay setting selected card and card play mode to allow animations to start
      setTimeout(() => {
        setSelectedCard(card);
        setSelectedPhrase('');
        setCustomPhrase('');
        setIsCardPlayMode(true);
        if (onCardPlayModeChange) {
          onCardPlayModeChange(true);
        }
        
        // Clear animation states after animation completes
        setTimeout(() => {
          setAnimatingCards({});
          setCardsToDiscard([]);
          
          // We'll let the backend response handle tutorial step transitions
          // This ensures consistency between frontend and backend tutorial state
          
          // The free_form step bubble will be shown by App.tsx after the backend confirms we're in that step
          // This ensures proper synchronization between frontend and backend state
        }, 600); // Animation duration
      }, 600); // Delay before showing play mode
    }
  };

  const handlePhraseSelect = (phrase: string) => {
    setSelectedPhrase(phrase);
    setCustomPhrase('');
    
    // Auto-submit when a suggested phrase is selected
    if (selectedCard && selectedCard.name) {
      onSubmitCardAction(selectedCard.name, phrase);
    }
  };

  const handleCustomPhraseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomPhrase(e.target.value);
    setSelectedPhrase('');
  };

  // Handle Enter key in the input field
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitAction();
    }
  };

  // Handle typing animation completion for tutorial step 4
  const handleTypingComplete = () => {
    setIsTypingComplete(true);
    setCustomPhrase(tutorialPredefinedPhrase);
    if (onTypingComplete) {
      onTypingComplete();
    }
  };

  const handleSubmitAction = () => {
    if (selectedCard && selectedCard.name && (selectedPhrase || customPhrase)) {
      // Use selected phrase or add the prefix to custom phrase
      let finalActionPhrase = selectedPhrase;
      
      // If using custom phrase, add the appropriate prefix based on card type
      if (!selectedPhrase && customPhrase) {
        if (selectedCard.type === 'ally' && selectedCard.ally_name && selectedCard.verb) {
          // For ally cards: "I ask [ally_name] to [verb] ..."
          finalActionPhrase = `I ask ${selectedCard.ally_name} to ${selectedCard.verb} ${customPhrase}`;
        } else {
          // For regular verb cards: "I [verb] ..."
          finalActionPhrase = `I ${selectedCard.verb} ${customPhrase}`;
        }
      }
      
      onSubmitCardAction(selectedCard.name, finalActionPhrase || '');
      
      // Reset the form and keep in card play mode, but now we'll show loading
      setSelectedPhrase('');
      setCustomPhrase('');
      
      // We keep the selectedCard until loading completes
      // The App component will handle setting isLoading to true
    }
  };

  const renderCardFront = (card: Card) => {
    const modifierValue = getConditionalModifierValue(card);

    return (
      <div className="card-front">
        <img
          src={assetUrl(`/assets/cards/${card.name}.png`)}
          alt={card.name}
          className="card-image"
        />
        {modifierValue !== null && (
          <div className="modifier-badge">
            +{modifierValue}
          </div>
        )}
      </div>
    );
  };

  const renderCardBack = (card: Card) => {
    const isChaosCard = card.type === 'chaos';
    const hasConsequences = card.consequences && card.consequences.length > 0;

    return (
      <div className="card-back">
        <h3>{formatCardName(card.name)}</h3>
        {hasConsequences && (
          <div className="card-consequences">
            {card.consequences!.map((c, i) => (
              <p
                key={i}
                className={`card-consequence-text ${c.is_positive === false ? 'negative' : 'positive'}`}
              >
                {c.description}
              </p>
            ))}
          </div>
        )}
        {card.description ? (
          <p className="card-description">{card.description}</p>
        ) : isChaosCard ? (
          <p className="card-instruction">Chaos Card<br/>Click to play</p>
        ) : (
          <p className="card-instruction">Hover to see verb<br/>Click to select</p>
        )}
      </div>
    );
  };

  const renderCard = (card: Card, index: number) => {
    const cardState = cardStates[card.name] || { isFlipped: false, isSelected: false };
    const isAnimating = animatingCards[card.name];
    const isDiscarding = cardsToDiscard.some(c => c.name === card.name);
    const isCardShaking = isShaking === card.name;
    
    // Calculate position for animations
    let animationClass = '';
    let animationStyle = {};
    
    if (isAnimating) {
      if (isCardShaking) {
        // Shake animation for chaos cards
        animationClass = 'card-shake';
      } else if (isDiscarding) {
        // Calculate position to animate to discard pile
        const discardPileRight = document.querySelector('.discard-pile');
        if (discardPileRight) {
          animationClass = 'card-to-discard';
        }
      } else if (cardState.isSelected) {
        // Move to the play position
        animationClass = 'card-to-play';
      }
    }
    
    // Add tutorial highlight class if this is the only card in tutorial mode
    const isTutorialHighlighted = isTutorialMode && 
                                deckStatus.hand.length === 1;
                                
    // Check if this is a chaos card
    const isChaosCard = card.type === 'chaos';
    
    return (
      <div 
        key={`${card.name}-${index}`}
        className={`card ${cardState.isFlipped ? 'flipped' : ''} ${cardState.isSelected ? 'selected' : ''} ${animationClass} ${isTutorialHighlighted ? 'tutorial-highlight' : ''} ${isChaosCard ? 'chaos-card' : ''}`}
        onMouseEnter={() => !isAnimating && handleCardHover(card.name, true)}
        onMouseLeave={() => !isAnimating && handleCardHover(card.name, false)}
        onClick={() => !isAnimating && handleCardClick(card)}
        style={{ 
          cursor: isAnimating ? 'default' : 'pointer',
          ...animationStyle
        }}
      >
        <div className="card-inner">
          {renderCardFront(card)}
          {renderCardBack(card)}
        </div>
      </div>
    );
  };

  // For tutorial mode, we might want to highlight a specific suggested phrase
  const isSuggestedPhraseHighlighted = (phrase: string): boolean => {
    if (!isTutorialMode || !selectedCard) return false;
    
    // If there's only one phrase, highlight it in tutorial mode
    const phrases = suggestedPhrases.card_phrases[selectedCard.name] || [];
    return phrases.length === 1 && phrase === phrases[0];
  };

  return (
    <div className={`card-game-container ${isVisible ? 'visible' : 'hidden'}`}>
      {showDiscardShuffle && (
        <div className="shuffle-animation">
          <div className="shuffle-text">Shuffling cards...</div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="loading-container">
          <img 
            src={assetUrl('/assets/ui/loading_story.png')}
            alt="Loading" 
            className="loading-image"
          />
          <p>Loading next section...</p>
        </div>
      ) : (
        <>
          {/* Regular card display when not in play mode */}
          {!isCardPlayMode ? (
            <div className="game-controls-container">
              <div className="draw-pile">
                <div className="card-stack">
                  {deckStatus.draw_pile.length > 0 && <div className="card-back-stack" />}
                </div>
                <div className="pile-info">
                  <h4>Draw Pile</h4>
                  <div className="pile-count">{deckStatus.draw_pile.length} cards</div>
                </div>
              </div>
              
              <div className="hand-container">
                <TransitionGroup className="hand">
                  {deckStatus.hand.map((card, index) => (
                    <CSSTransition key={`${card.name}-${index}`} timeout={500} classNames="card-transition">
                      {renderCard(card, index)}
                    </CSSTransition>
                  ))}
                </TransitionGroup>
              </div>
              
              <div className="discard-pile">
                <div className="card-stack">
                  {deckStatus.discard_pile.length > 0 && <div className="card-back-stack" />}
                </div>
                <div className="pile-info">
                  <h4>Discard</h4>
                  <div className="pile-count">{deckStatus.discard_pile.length} cards</div>
                </div>
              </div>
            </div>
          ) : (
            /* Card play mode */
            <div className="action-panel">
              <div className="selected-card-display">
                <img 
                  src={selectedCard && selectedCard.name ? assetUrl(`/assets/cards/${selectedCard.name}.png`) : ''}
                  alt={selectedCard?.name || 'Selected card'}
                  className="selected-card-image"
                />
                <div className="card-info">
                  <h3>{selectedCard && selectedCard.name ? formatCardName(selectedCard.name) : ''}</h3>
                  <p>Choose an action to perform</p>
                </div>
              </div>
              
              <div className="suggested-phrases">
                <h4>Suggested Actions</h4>
                <ul>
                  {selectedCard && selectedCard.name && suggestedPhrases.card_phrases[selectedCard.name] ? 
                    suggestedPhrases.card_phrases[selectedCard.name].map((phrase, index) => (
                      <li 
                        key={index} 
                        className={`${selectedPhrase === phrase ? 'selected' : ''} ${isSuggestedPhraseHighlighted(phrase) ? 'tutorial-highlight' : ''}`}
                        onClick={() => handlePhraseSelect(phrase)}
                        title={phrase} // Show full phrase on hover
                        style={{ animationDelay: `${index * 0.05}s` }} // Staggered animation
                      >
                        {phrase}
                      </li>
                    )) : 
                    <li>No suggestions</li>
                  }
                </ul>
                
                {/* Show custom phrase input only in free-form step or after tutorial */}
                {isInFreeFormStep && (
                  <div className="custom-phrase">
                    <span className="custom-phrase-prefix">
                      {selectedCard?.type === 'ally'
                        ? `I ask ${selectedCard?.ally_name || 'Alfie'} to ${selectedCard?.verb || ''} `
                        : `I ${selectedCard?.verb || ''} `}
                    </span>
                    {/* Tutorial step 4: Show typing animation */}
                    {isTutorialMode && tutorialStep === 4 && tutorialPredefinedPhrase ? (
                      <div className="tutorial-typing-container">
                        <TypingAnimation
                          text={tutorialPredefinedPhrase}
                          speed={120}
                          onComplete={handleTypingComplete}
                          showCursor={true}
                        />
                      </div>
                    ) : (
                      /* Normal free-form input */
                      <input
                        type="text"
                        value={customPhrase}
                        onChange={handleCustomPhraseChange}
                        onKeyPress={handleKeyPress}
                        placeholder="type your own..."
                        disabled={isLoading}
                      />
                    )}
                  </div>
                )}
                
                <div className="action-controls">
                  <button
                    className="submit-action-btn"
                    onClick={handleSubmitAction}
                    disabled={
                      isLoading ||
                      !(selectedPhrase || customPhrase) ||
                      (isTutorialMode && tutorialStep === 4 && !isTypingComplete)
                    }
                  >
                    {isLoading ? 'Loading...' : 'Play Card'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CardDeck;